import React from 'react';
import { Box } from '@mui/material';
import { Badge, badgeStyle, badgeType, colors, fontWeight, Icons, Text, textLevel } from '../../../index';
import { btnType } from '../../atoms/button';
import { IconButton } from '../icon-button';
import ProfileInfo, { ProfileInfoRole } from '../../../../components/v2/ProfileInfo';
import { sxStyles, useStyles } from './MedicationCard.styles';
import { useSelector } from 'react-redux';
import { getAuth } from '../../../../redux/modules/auth/selectors';
import { getProfile } from '../../../../redux/modules/profile/selectors';
import dayjs from '../../../../utils/dayjs';
import { getImportanceLevelDisplayText } from '../../../../utils/medicationUtils';

const MedicationCard = ({
  medication,
  onUpdateClick,
  onRefillClick,
  onDiscontinueClick,
  onMarkInactive,
  isSavingMedication,
  preferredPharmacyName,
  sx,
  onClick,
}) => {
  const classes = useStyles();
  const {
    tagMetaData,
    currentImportanceLevel,
    updatedAt,
    assignedBy,
    userDetails,
    assignedAt,
    taxonomyName,
    note,
  } = medication;
  const { isAdmin } = useSelector(getAuth);
  const { profile } = useSelector(getProfile);

  const drugName = taxonomyName || '';
  const drugNameExternal = note || taxonomyName || 'Unknown';
  const externalMedicationName = tagMetaData?.rxDrugInfo?.externalMedicationName || '';
  const dose = tagMetaData?.rxDrugInfo?.dose;
  const sig = tagMetaData?.rxDrugInfo?.sig;
  const isActiveOrDiscontinued =
    currentImportanceLevel === 'ACTIVE' || currentImportanceLevel === 'DISCONTINUED';

  const getDateByStatus = () => {
    switch (currentImportanceLevel) {
      case 'DISCONTINUED':
        return tagMetaData.rxDrugInfo.discontinuedAt;
      default:
        return updatedAt || assignedAt;
    }
  };
  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        ...sxStyles.cardShadow,
        ...sxStyles.cardBorder(currentImportanceLevel),
        ...sx,
      }}
      gap="16px"
    >
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        paddingTop="24px"
        paddingRight="32px"
        paddingLeft="32px"
      >
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Box
            display="flex"
            flexDirection="row"
            alignItems="flex-start"
            gap="24px"
            flex={1}
            minWidth={0}
            onClick={onClick}
            sx={{ cursor: onClick ? 'pointer' : 'default' }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor={colors.primary50}
              border={`1px solid ${colors.primary200}`}
              borderRadius="4px"
              padding="8px"
              width="40px"
              height="40px"
              flexShrink={0}
            >
              <Icons glyph="medication-2" className={classes.icon} />
            </Box>

            <Box display="flex" flexDirection="column" gap="4px" minWidth={0} flex={1}>
              <Text sx={sxStyles.name} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                {currentImportanceLevel?.toLowerCase().includes('external') ? drugNameExternal : drugName}
              </Text>
              {externalMedicationName && sig && !currentImportanceLevel?.toLowerCase().includes('external') ? (
                <Text
                  level={textLevel.S}
                  weight={fontWeight.MEDIUM}
                  color={colors.neutral600}
                  sx={sxStyles.sigLineHeight}
                >
                  {externalMedicationName} - {dose} - {sig}
                </Text>
              ) : (
                <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral600}>
                  {sig || 'Unknown'}
                </Text>
              )}
            </Box>
          </Box>

          <Box flexShrink={0}>
            <Badge
              variant={badgeType.FILLED}
              style={badgeStyle[currentImportanceLevel] || badgeStyle.UNRELATED}
              className={classes.badge}
            >
              {getImportanceLevelDisplayText(currentImportanceLevel)}
            </Badge>
          </Box>
        </Box>
      </Box>

      {currentImportanceLevel === 'DISCONTINUED' && (
        <Box display="flex" paddingRight="32px" paddingBottom="4px" paddingLeft="32px">
          <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.destructive600}>
            Reason for discontinuation:{' '}
            <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral600}>
              {medication?.tagMetaData?.rxDrugInfo?.discontinuedReason}
            </Text>
          </Text>
        </Box>
      )}

      {(isActiveOrDiscontinued || currentImportanceLevel === 'E_PRESCRIPTION_FAILED') && (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            paddingRight="32px"
            paddingBottom="8px"
            paddingLeft="32px"
          >
            <Box display="flex" flexDirection="row" alignItems="center" flex={1} gap="8px">
              {currentImportanceLevel === 'ACTIVE' ? (
                <Icons glyph="checkmark" color={colors.success600} className={classes.sentIcon} />
              ) : currentImportanceLevel !== 'DISCONTINUED' ? (
                <Icons glyph="close" color={colors.destructive600} className={classes.sentIcon} />
              ) : null}
              <Text
                sx={sxStyles.status}
                weight={fontWeight.MEDIUM}
                color={currentImportanceLevel === 'ACTIVE' ? colors.success600 : colors.destructive600}
              >
                {currentImportanceLevel === 'ACTIVE'
                  ? 'Successfully sent'
                  : currentImportanceLevel === 'DISCONTINUED'
                  ? 'Discontinued'
                  : 'Failed to send'}{' '}
                <Text sx={sxStyles.status} weight={fontWeight.MEDIUM} color={colors.neutral600}>
                  {'on ' + dayjs(getDateByStatus()).format('MM/DD/YY')}
                  {currentImportanceLevel !== 'DISCONTINUED' &&
                    ' at ' + dayjs(getDateByStatus()).format('h:mma')}
                </Text>
              </Text>
            </Box>
            {currentImportanceLevel !== 'DISCONTINUED' && preferredPharmacyName && preferredPharmacyName !== '' && (
              <Box display="flex" flexDirection="row" alignItems="center" flex={1} gap="8px">
                <Icons glyph="sent" color={colors.primary500} className={classes.sentIcon} />
                <Text sx={sxStyles.status} weight={fontWeight.MEDIUM} color={colors.neutral600}>
                  Sent to{' '}
                  <Text sx={sxStyles.status} weight={fontWeight.MEDIUM} color={colors.primary600}>
                    {preferredPharmacyName || ''}
                  </Text>
                </Text>
              </Box>
            )}
          </Box>
        )}

      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        borderTop={`1px solid ${colors.neutral100}`}
        paddingTop="24px"
        paddingRight="32px"
        paddingBottom="24px"
        paddingLeft="32px"
      >
        {!currentImportanceLevel?.toLowerCase().includes('external') ? (
          <ProfileInfo
            type="provider"
            isProvider={true}
            photo={assignedBy?.profileImage || userDetails?.profileImage || ''}
            profilePhotoName={assignedBy?.name || userDetails?.name || 'Provider'}
            fullName={assignedBy?.name || userDetails?.name || 'Provider'}
            role={assignedBy?.designation || userDetails?.designation || 'Provider'}
          />
        ) : (
          <Box display="flex" flexDirection="column" gap="4px">
            <Text level={textLevel.S} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
              Prescribed by External Prescriber
            </Text>
            <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral600}>
              {medication?.tagMetaData?.rxDrugInfo?.providerName || 'Unknown'}
            </Text>
          </Box>
        )}
        {(profile.designation === ProfileInfoRole.PHYSICIAN ||
          profile.designation === ProfileInfoRole.NURSE_PRACTITIONER ||
          isAdmin) && (
          <Box display="flex" flexDirection="row" alignItems="center" gap="16px">
            {currentImportanceLevel === 'ACTIVE' && (
              <IconButton variant={btnType.OUTLINE} onClick={onRefillClick} sx={sxStyles.outlineBtn(true)}>
                <Text level={textLevel.S} weight={fontWeight.BOLD} color={colors.primary600}>
                  Send Refill RX
                </Text>
              </IconButton>
            )}
            <IconButton variant={btnType.OUTLINE} onClick={onUpdateClick} sx={sxStyles.outlineBtn(true)}>
              <Text level={textLevel.S} weight={fontWeight.BOLD} color={colors.primary600}>
                Update RX
              </Text>
            </IconButton>
            {currentImportanceLevel === 'ACTIVE' && (
              <IconButton
                variant={btnType.OUTLINE}
                onClick={onDiscontinueClick}
                sx={sxStyles.outlineBtn(false)}
              >
                <Text level={textLevel.S} weight={fontWeight.BOLD} color={colors.destructive600}>
                  Discontinue RX
                </Text>
              </IconButton>
            )}
            {currentImportanceLevel === 'ACTIVE_EXTERNAL_RX' && (
              <IconButton
                variant={btnType.OUTLINE}
                disabled={isSavingMedication}
                onClick={onMarkInactive}
                sx={sxStyles.outlineBtn(false)}
              >
                <Text level={textLevel.S} weight={fontWeight.BOLD} color={colors.destructive600}>
                  Mark Inactive
                </Text>
              </IconButton>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MedicationCard;
